import { createContext, useContext, ReactNode } from 'react';
import { useLiveExecution, NodeExecution, LogEntry } from './useLiveExecution';

interface Execution {
  id: string;
  workflow_id: string;
  status: 'running' | 'success' | 'failed' | 'cancelled';
  trigger_type: string;
  created_at: number;
  completed_at?: number;
  error?: string;
}

interface LiveExecutionContextValue {
  executionId: string | null;
  status: 'idle' | 'running' | 'success' | 'failed' | 'cancelled';
  startedAt: number | null;
  completedAt: number | null;
  nodeExecutions: NodeExecution[];
  nodesLatestFirst: NodeExecution[];
  selectedNodeId: string | null;
  selectedNode: NodeExecution | null;
  logs: LogEntry[];
  isLoading: boolean;
  stats: {
    completedCount: number;
    failedCount: number;
    runningCount: number;
    totalCount: number;
  };
  duration: number;
  selectNode: (nodeId: string | null) => void;
  clearUserSelection: () => void;
}

const LiveExecutionContext = createContext<LiveExecutionContextValue | null>(null);

interface LiveExecutionProviderProps {
  currentExecution: Execution | null | undefined;
  children: ReactNode;
}

export function LiveExecutionProvider({ currentExecution, children }: LiveExecutionProviderProps) {
  const executionState = useLiveExecution({ currentExecution });

  return (
    <LiveExecutionContext.Provider value={executionState}>
      {children}
    </LiveExecutionContext.Provider>
  );
}

export function useLiveExecutionContext() {
  const context = useContext(LiveExecutionContext);
  if (!context) {
    throw new Error('useLiveExecutionContext must be used within a LiveExecutionProvider');
  }
  return context;
}
